import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AgpSharedAggregatorComponentRegistryModule } from '@liberty-financial/agp-shared';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';

import { DiaryNotesModule } from '@features/diary-notes/diary-notes.module';
import { DiaryNoteService } from '@features/diary-notes/services/diary-note.service';
import { TaskConfig, TASK_CONFIG } from '@features/tasks/services/task-config';
import { TaskService } from '@features/tasks/services/task.service';
import { TasksModule } from '@features/tasks/tasks.module';
import { SparkBusinessModule } from '@shared/components/business/business.module';
import { SparkSharedContactNumberModule } from '@shared/components/contact-number/contact-number.module';
import { ContactSearchModule } from '@shared/components/contact-search/contact-search.module';
import { SparkPlusDropdownModule } from '@shared/components/dropdown/dropdown.module';
import { FormFieldsModule } from '@shared/components/form-fields/form-fields.module';
import { LoadModule } from '@shared/components/load/load.module';
import { MenuModule } from '@shared/components/menu/menu.module';
import { SparkPlusModalModule } from '@shared/components/modal/modal.module';
import { SparkSharedPercentageModule } from '@shared/components/percentage/percentage.module';
import { ProviderImageModule } from '@shared/components/provider-image/provider-image.module';
import { SparkSharedQuestionModule } from '@shared/components/question/question.module';
import { SearchModule } from '@shared/components/search/search.module';
import { SummaryFieldModule } from '@shared/components/summary-field/summary-field.module';
import { FilterPipe } from '@shared/pipes/filter-pipe';
import { PipesModule } from '@shared/pipes/pipes.module';
import { SharedModule } from '@shared/shared.module';

import { CompanyDirectorsComponent } from './components/company/directors/directors.component';
import { CompanyShareholdersComponent } from './components/company/shareholders/shareholders.component';
import { CompanySummaryPanelComponent } from './components/company/summary-panel/company-summary-panel.component';
import { ContactListItemComponent } from './components/contact/contact-list-item/contact-list-item.component';
import { ContactListViewComponent } from './components/contact/contact-list-view/contact-list-view.component';
import { SparkContactFiltersComponent } from './components/contact/filters/filters.component';
import { IndividualDependentsComponent } from './components/individual/dependents/dependents.component';
import { IndividualRelationshipsComponent } from './components/individual/relationships/relationships.component';
import { IndividualResidencesComponent } from './components/individual/residences/residences.component';
import { IndividualSummaryPanelComponent } from './components/individual/summary-panel/individual-summary-panel.component';
import { ContactReferrerArrangementFormComponent } from './components/referrer-arrangement/form/form.component';
import { ContactsReferrerArrangementComponent } from './components/referrer-arrangement/referrer-arrangement.component';
import { ReferrerArrangementSummaryComponent } from './components/referrer-arrangement/summary-view.component';
import { TrustBeneficiariesComponent } from './components/trust/beneficiaries/beneficiaries.component';
import { TrustSummaryPanelComponent } from './components/trust/summary-panel/trust-summary-panel.component';
import { TrustTrusteesComponent } from './components/trust/trustees/trustees.component';
import { ContactsRoutingModule } from './contacts-routing';
import { ContactNamePipe } from './pipes/contactName';
import { DisplayAffiliateId } from './pipes/display-affiliateId.pipe';
import { ReferrerArrangementDisabledPipe } from './pipes/referrer-arrangement-disabled.pipe';
import { ReferrerArrangementHiddenPipe } from './pipes/referrer-arrangement-hidden.pipe';
import { ContactDiaryNoteService } from './services/diary-note.service';
import { ContactTaskService } from './services/task.service';
import { CompanyEditComponent } from './views/company/details/edit/company-edit.component';
import { CompanyNewComponent } from './views/company/details/new/company-new.component';
import { CompanyLayoutComponent } from './views/company/layout/company-layout.component';
import { CompanySummaryComponent } from './views/company/summary/company-summary.component';
import { EntityTypeSelectorComponent } from './views/contact/entity-type-selector/entity-type-selector.component';
import { ContactListComponent } from './views/contact/list/contact-list.component';
import { ClientOpportunityListComponent } from './views/contact/opportunity-list/contact-opportunity-list.component';
import { ClientProductListComponent } from './views/contact/product-list/product-list.component';
import { ContactReassignComponent } from './views/contact/reassign/reassign.component';
import { IndividualEditComponent } from './views/individual/details/edit/individual-edit.component';
import { IndividualNewComponent } from './views/individual/details/new/individual-new.component';
import { IndividualLayoutComponent } from './views/individual/layout/individual-layout.component';
import { IndividualSummaryComponent } from './views/individual/summary/individual-summary.component';
import { TrustEditComponent } from './views/trust/details/edit/trust-edit.component';
import { TrustNewComponent } from './views/trust/details/new/trust-new.component';
import { TrustLayoutComponent } from './views/trust/layout/trust-layout.component';
import { TrustSummaryComponent } from './views/trust/summary/trust-summary.component';
import { SparkCoreModule } from '../../core/core.module';
import { AuditModule } from '@shared/components/audit/audit.module';
import { IndividualAuditListComponent } from './views/audit/individual-audit-list.component';
import { CompanyAuditListComponent } from './views/audit/company-audit-list.component';
import { TrustAuditListComponent } from './views/audit/trust-audit-list.component';

const taskConfig: TaskConfig = {
  add: {
    saveLabel: 'Add',
    title: 'Add Task'
  },
  edit: {
    saveLabel: 'Save',
    title: 'Edit Task'
  }
};

@NgModule({
  declarations: [
    ContactListComponent,
    ContactListViewComponent,
    ContactListItemComponent,
    IndividualNewComponent,
    IndividualEditComponent,
    IndividualSummaryComponent,
    EntityTypeSelectorComponent,
    CompanyNewComponent,
    CompanyEditComponent,
    CompanySummaryComponent,
    TrustNewComponent,
    TrustEditComponent,
    TrustSummaryComponent,
    TrustSummaryPanelComponent,
    ClientProductListComponent,
    IndividualLayoutComponent,
    IndividualSummaryPanelComponent,
    IndividualResidencesComponent,
    IndividualRelationshipsComponent,
    IndividualDependentsComponent,
    ContactsReferrerArrangementComponent,
    ReferrerArrangementSummaryComponent,
    ContactReferrerArrangementFormComponent,
    CompanyDirectorsComponent,
    CompanyShareholdersComponent,
    CompanyLayoutComponent,
    CompanySummaryPanelComponent,
    TrustSummaryPanelComponent,
    TrustLayoutComponent,
    ClientOpportunityListComponent,
    ContactReassignComponent,
    SparkContactFiltersComponent,
    ContactNamePipe,
    DisplayAffiliateId,
    ReferrerArrangementDisabledPipe,
    ReferrerArrangementHiddenPipe,
    TrustTrusteesComponent,
    TrustBeneficiariesComponent,
    IndividualAuditListComponent,
    CompanyAuditListComponent,
    TrustAuditListComponent
  ],
  exports: [IndividualEditComponent, ContactListViewComponent],
  providers: [
    FilterPipe,
    {
      provide: TASK_CONFIG,
      useValue: taskConfig
    },
    {
      provide: TaskService,
      useClass: ContactTaskService
    },
    {
      provide: DiaryNoteService,
      useClass: ContactDiaryNoteService
    }
  ],
  imports: [
    CommonModule,
    BsDropdownModule.forRoot(),
    SharedModule,
    ContactsRoutingModule,
    SparkPlusModalModule,
    FormsModule,
    FormFieldsModule,
    ModalModule.forRoot(),
    ReactiveFormsModule,
    SearchModule,
    LoadModule,
    MenuModule,
    PipesModule,
    SummaryFieldModule,
    ProviderImageModule,
    ContactSearchModule,
    SparkPlusDropdownModule,
    TasksModule,
    DiaryNotesModule,
    SparkCoreModule,
    SparkSharedPercentageModule,
    SparkSharedContactNumberModule,
    SparkSharedQuestionModule,
    AgpSharedAggregatorComponentRegistryModule,
    SparkBusinessModule,
    AuditModule
  ]
})
export class ContactsModule {}
